import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, articleMeta, absoluteUrl } from "@/lib/seo";
import { articleHeroFor } from "@/lib/article-heroes";
import { BlogPost, articleJsonLd, breadcrumbJsonLd } from "@/components/BlogPost";

const SLUG = "montessori-kalusteet-kotiin";
const URL = `https://montessori24.fi/montessori-koulu/${SLUG}`;
const TITLE = "Montessori-kalusteet kotiin — Mistä aloittaa";
const DESC = "Montessori-kalusteet kotiin: 6-vaiheinen aloitusopas. Lapsen mittakaavainen huone, oppimistorni, lattialta sänky ja avoimet hyllyt. Käytännön vinkit.";
const PUBLISHED = "2026-06-18";
const HERO = articleHeroFor(SLUG);

export const Route = createFileRoute("/montessori-koulu/montessori-kalusteet-kotiin")({
  head: () => ({
    meta: articleMeta({
      slug: SLUG,
      title: TITLE,
      description: DESC,
      published: PUBLISHED,
      image: HERO,
    }),
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleJsonLd({ title: TITLE, description: DESC, url: URL, image: absoluteUrl(HERO), publishedISO: PUBLISHED })) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd(SLUG, TITLE)) },
    ],
  }),
  component: Post,
});

function Post() {
  return (
    <BlogPost
      eyebrow="Aloittajan opas"
      title={TITLE}
      lead="Et tarvitse koko taloa täynnä Montessori-kalusteita. Tarvitset yhden hyvin valitun aloituksen ja rohkeuden katsoa kotiasi lapsen silmin. Tässä kuusivaiheinen opas, jolla tuot Montessori-pedagogiikan kotiin ilman täydellisyyspaineita."
      readingMinutes={8}
      publishedISO={PUBLISHED}
    >
      <h2>1. Kumarru lapsen tasolle — kirjaimellisesti</h2>
      <p>Ennen ostamista, mene polvillesi keskelle olohuonetta. Mitä näet? Aikuisten kahvakuppeja, johtoja, ovenkahvoja yläpuolellasi. Tämä on Montessorin lähtökohta: <strong>lapsi näkee maailman alhaalta</strong>, joten myös kalusteiden tulee skaalautua sinne.</p>

      <h2>2. Ensimmäinen hankinta: oppimistorni</h2>
      <p>Jos voit hankkia vain yhden Montessori-kalusteen, hanki oppimistorni. Se nostaa 1–5-vuotiaan turvallisesti keittiön työtasolle, jolloin lapsi pääsee mukaan ruoanlaittoon, leipomiseen ja käsienpesuun. Oppimistorni on Montessorin <strong>”osallisuuden työkalu”</strong> — se muuttaa lapsen sivustaseuraajasta tekijäksi.</p>
      <p><Link to="/kauppa" search={{ kategoria: "tower" }}>Katso oppimistornit →</Link></p>

      <h2>3. Lapsen kokoinen pöytä ja tuoli</h2>
      <p>Sääntö on yksinkertainen: <strong>jalat lattiassa, kädet pöydällä, polvet 90°</strong>. 1-vuotiaalle riittää 25 cm korkea tuoli, 3-vuotiaalle 30 cm, 5-vuotiaalle 35 cm. Pieni työpiste lastenhuoneen nurkassa muuttaa keskittymisen kestoa dramaattisesti — kun keho ei taistele kalustetta vastaan, mieli saa keskittyä tehtävään.</p>

      <h2>4. Avoimet matalat hyllyt — ei laatikoita</h2>
      <p>Montessorin sydän on järjestys, ja järjestys edellyttää näkyvyyttä. Vaihda iso lelulaatikko 2–3 matalaan hyllyyn, joille mahtuu 5–8 lelua kerrallaan. Kierrätä loput pois näkyviltä ja vaihda valikoimaa 2 viikon välein. Lapsi keskittyy paremmin vähempään.</p>

      <h2>5. Lattiasänky tai matala sänky</h2>
      <p>Montessori-pinnasängyt vaihtuvat usein <strong>floor bediin</strong> noin 18 kk iässä. Lapsi saa nousta ja mennä nukkumaan itse, mikä vahvistaa autonomiaa ja paradoksaalisesti rauhoittaa unirytmiä — pakon kokemus poistuu.</p>

      <h2>6. Pikler-kiipeilyteline liikkumiseen</h2>
      <p>Yksi kaluste, joka ratkaisee koko karkeamotoriikan: <Link to="/montessori-koulu/pikler-kiipeilyteline-opas">Pikler-kiipeilyteline</Link>. Ikäsuositus vaihtelee mallin ja valmistajan mukaan — tarkista tuotekohtainen ikäsuositus tuotesivulta. Lue erillinen Pikler-opas yksityiskohdista.</p>

      <h2>Budjetti — paljonko maksaa?</h2>
      <table>
        <thead><tr><th>Kaluste</th><th>Hinta</th><th>Käyttöikä</th></tr></thead>
        <tbody>
          <tr><td>Oppimistorni</td><td>129–229 €</td><td>1–5 v</td></tr>
          <tr><td>Lapsen pöytä + tuoli</td><td>120–200 €</td><td>1–6 v</td></tr>
          <tr><td>Matala hylly (2–3 kpl)</td><td>80–150 € / kpl</td><td>0–10 v</td></tr>
          <tr><td>Pikler-kolmio</td><td>150–250 €</td><td>0,5–6 v</td></tr>
          <tr><td>Lattiasänky</td><td>150–400 €</td><td>1–6 v</td></tr>
        </tbody>
      </table>
      <p>Et tarvitse kaikkea yhtä aikaa. Hanki yksi kaluste, anna lapsen sopeutua ja päätä sitten seuraavasta.</p>

      <h2>Yleisimmät virheet</h2>
      <ul>
        <li><strong>Liian täysi huone.</strong> Montessori-tila on harkitun tyhjä. Vähemmän on enemmän.</li>
        <li><strong>Muovileluja avoimilla hyllyillä.</strong> Vaihda 60 % puuhun ja luonnonmateriaaleihin — aisteilla on väliä.</li>
        <li><strong>Aikuinen tekee puolesta.</strong> Kalusteet ovat siellä, jotta lapsi pärjää itse. Hidasta, odota.</li>
      </ul>

      <p><Link to="/kauppa">Selaa koko Montessori-mallistoa →</Link></p>
    </BlogPost>
  );
}